import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { getUserPreferences, getMasterData } from "./service";
import { getDecodedToken } from "../../utils/localStorage";
import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { getAsset, getLabel } from "../../utils/appConfig";
import useStore from "../../store/useStore";
import LoadingSkeleton from "./components/states/LoadingSkeleton";

// Route guard component that redirects users without preferences to PersonalizeFeed
const TatvaPediaRouteGuard = ({ children }: { children: React.ReactNode }) => {
  const [loading, setLoading] = useState(true);
  const [hasPreferences, setHasPreferences] = useState(false);
  const location = useLocation();
  // Get the appropriate logo based on app configuration
  const pediaLogo = getAsset("pedia");
  const pediaLabel = getLabel("pediaLabel");
  const {
    setPediaUserPreferences,
    setSelectedTatvaPediaFilters,
    tatvaPediaFilters,
    setTatvaPediaFilters,
    pediaUserPreferences,
  } = useStore();

  // Helper function to extract IDs from preference strings
  const extractIds = (prefString: string) => {
    if (!prefString) return [];

    // Extract the content within parentheses
    const match = prefString.match(/\((.*?)\)/);
    if (!match || !match[1]) return [];

    // Split by "OR" and trim to get clean IDs
    return match[1].split("OR").map((id) => id.trim());
  };

  // Helper function to map extracted IDs to filter objects
  const mapIdsToFilterObjects = (
    ids: string[],
    filterItems: any[],
    filterKey: string,
    nameField: string = "code"
  ) => {
    if (!filterItems?.length) return [];

    return ids
      .map((id) => {
        const item = filterItems.find((item) => item.id == id);
        return item
          ? {
              id: item.id,
              name: nameField === "displayName" ? item.displayName : item.code,
              ...(item.depends && { depends: item.depends }),
            }
          : null;
      })
      .filter(Boolean);
  };

  useEffect(() => {
    const checkUserPreferencesAndLoadFilters = async () => {
      try {
        // Skip the check if we're already on the personalize feed page
        if (location.pathname.includes("/personalize-feed")) {
          setLoading(false);
          return;
        }

        // Check if preferences are already loaded in the store
        if (
          pediaUserPreferences &&
          pediaUserPreferences.specialities &&
          Object.keys(pediaUserPreferences).length > 0
        ) {
          setHasPreferences(true);

          // If filters are also already loaded, we can skip all API calls
          const filtersAlreadyLoaded =
            tatvaPediaFilters &&
            Object.values(tatvaPediaFilters).some(
              (arr) => arr && arr.length > 0
            );

          if (filtersAlreadyLoaded) {
            // Update selected filters with current preferences
            const specialityIds = extractIds(pediaUserPreferences.specialities || "");
            const genreIds = extractIds(pediaUserPreferences.genres || "");
            const continuumIds = extractIds(pediaUserPreferences.continuums || "");
            const typeIds = extractIds(pediaUserPreferences.types || "");
            const interestpreferenceIds = extractIds(pediaUserPreferences.topics || "");
            const consolidatedModuleIds = extractIds(pediaUserPreferences.consolidatedmodules || "");
            
            const formattedSelectedItems = {
              "Content Type": mapIdsToFilterObjects(
                typeIds,
                tatvaPediaFilters["Content Type"],
                "Content Type"
              ),
              Genre: mapIdsToFilterObjects(
                genreIds,
                tatvaPediaFilters["Genre"],
                "Genre"
              ),
              Continuum: mapIdsToFilterObjects(
                continuumIds,
                tatvaPediaFilters["Continuum"],
                "Continuum"
              ),
              Speciality: mapIdsToFilterObjects(
                specialityIds,
                tatvaPediaFilters["Speciality"],
                "Speciality",
                "displayName"
              ),
              Interestpreference: mapIdsToFilterObjects(
                interestpreferenceIds,
                tatvaPediaFilters["Interestpreference"],
                "Interestpreference"
              ),
              ConsolidatedModule: mapIdsToFilterObjects(
                consolidatedModuleIds,
                tatvaPediaFilters["ConsolidatedModule"],
                "ConsolidatedModule"
              ),
            };
            
            setSelectedTatvaPediaFilters(formattedSelectedItems);
            setLoading(false);
            return;
          }
        }

        // Only proceed with API calls if we need to
        setLoading(true);
        const tokenData: any = getDecodedToken();
        let prefsData = pediaUserPreferences;

        // Fetch user preferences if not already in store
        if (!prefsData?.specialities && tokenData?.user?.b2cId) {
          const preferences = await getUserPreferences(tokenData.user.b2cId);
          if (preferences?.status === 200) {
            prefsData = preferences?.body;
            setPediaUserPreferences(prefsData);
            setHasPreferences(!!prefsData?.specialities);
          }
        } else if (prefsData?.specialities) {
          setHasPreferences(true);
        }

        // Only proceed to fetch and map filters if we have preferences
        if (prefsData?.specialities) {
          // Fetch filters directly in the route guard
          const filterResponse = await getMasterData([
            "ContentType",
            "ContentGenre",
            "Continuum",
            "Speciality",
            "Interestpreference",
            "ConsolidatedModule"
          ]);

          // Map API response to our filter structure
          const mappedData = {
            "Content Type": filterResponse.contenttype || [],
            Genre: filterResponse.contentgenre || [],
            Continuum: filterResponse.continuum || [],
            Speciality: filterResponse.speciality || [],
            Interestpreference: filterResponse.interestpreference || [],
            ConsolidatedModule: filterResponse.consolidatedmodule || [],
          };

          // Update the store with filters
          setTatvaPediaFilters(mappedData);

          // Extract IDs from preference strings
          const specialityIds = extractIds(prefsData.specialities || "");
          const genreIds = extractIds(prefsData.genres || "");
          const continuumIds = extractIds(prefsData.continuums || "");
          const typeIds = extractIds(prefsData.types || "");
          const interestpreferenceIds = extractIds(prefsData.topics || "");
          const consolidatedModuleIds = extractIds(prefsData.consolidatedmodules || "");
          // Map IDs to the actual filter objects
          const formattedSelectedItems = {
            "Content Type": mapIdsToFilterObjects(
              typeIds,
              mappedData["Content Type"],
              "Content Type"
            ),
            Genre: mapIdsToFilterObjects(
              genreIds,
              mappedData["Genre"],
              "Genre"
            ),
            Continuum: mapIdsToFilterObjects(
              continuumIds,
              mappedData["Continuum"],
              "Continuum"
            ),
            Speciality: mapIdsToFilterObjects(
              specialityIds,
              mappedData["Speciality"],
              "Speciality",
              "displayName"
            ),
            Interestpreference: mapIdsToFilterObjects(
              interestpreferenceIds,
              mappedData["Interestpreference"],
              "Interestpreference"
            ),
            ConsolidatedModule: mapIdsToFilterObjects(
              consolidatedModuleIds,
              mappedData["ConsolidatedModule"],
              "ConsolidatedModule"
            ),
          };
          // Update the selected filters in the store
          setSelectedTatvaPediaFilters(formattedSelectedItems);
        }
      } catch (error) {
        console.error(
          "Error checking user preferences or loading filters:",
          error
        );
        setHasPreferences(false);
      } finally {
        setLoading(false);
      }
    };

    checkUserPreferencesAndLoadFilters();
  }, [
    location.pathname,
    tatvaPediaFilters,
    setPediaUserPreferences,
    setTatvaPediaFilters,
    setSelectedTatvaPediaFilters,
  ]);

  if (loading) {
    return <LoadingSkeleton />;
  }

  // If we're trying to go to the TatvaPedia home but don't have preferences,
  // redirect to personalize feed
  if (!hasPreferences && !location.pathname.includes("/personalize-feed")) {
    return <Navigate to="/tatvapedia/personalize-feed" replace />;
  }

  return <>{children}</>;
};

export default TatvaPediaRouteGuard;
